import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import notify from 'components/atoms/Notify'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'

const normalizeCompanyBranches = data => {
  // eslint-disable-next-line
  if (Array.isArray(data)) return data
  // eslint-disable-next-line
  if (Array.isArray(data?.branch)) return data.branch
  // eslint-disable-next-line
  if (Array.isArray(data?.branches)) return data.branches
  return []
}

const PriceControlModal = ({ open, onClose, companyId }) => {
  const { t } = useTranslation()
  const [branchOptions, setBranchOptions] = useState([])
  const [selectedBranchIds, setSelectedBranchIds] = useState([])
  const [isDailyActive, setIsDailyActive] = useState(true)
  const [isWeeklyActive, setIsWeeklyActive] = useState(true)
  const [isMonthlyActive, setIsMonthlyActive] = useState(true)
  const [isYearlyActive, setIsYearlyActive] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !companyId) return

    const fetchBranches = async () => {
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.success) {
        setBranchOptions(
          normalizeCompanyBranches(response.data).map(branch => ({
            id: branch._id,
            name: branch.name,
          }))
        )
      } else {
        setBranchOptions([])
      }
    }

    setSelectedBranchIds([])
    fetchBranches()
  }, [open, companyId])

  const activeTierCount = [isDailyActive, isWeeklyActive, isMonthlyActive, isYearlyActive].filter(Boolean).length

  const trySetTier = (nextFlags, apply) => {
    if (nextFlags.filter(Boolean).length === 0) {
      notify.error(t('priceControlModal.messages.atLeastOneTier'))
      return
    }
    apply()
  }

  const setDaily = checked => trySetTier([checked, isWeeklyActive, isMonthlyActive, isYearlyActive], () => setIsDailyActive(checked))
  const setWeekly = checked => trySetTier([isDailyActive, checked, isMonthlyActive, isYearlyActive], () => setIsWeeklyActive(checked))
  const setMonthly = checked => trySetTier([isDailyActive, isWeeklyActive, checked, isYearlyActive], () => setIsMonthlyActive(checked))
  const setYearly = checked => trySetTier([isDailyActive, isWeeklyActive, isMonthlyActive, checked], () => setIsYearlyActive(checked))

  const handleUpdate = async () => {
    if (!selectedBranchIds.length) {
      notify.error(t('priceControlModal.messages.selectBranch'))
      return
    }
    if (activeTierCount === 0) {
      notify.error(t('priceControlModal.messages.atLeastOneTier'))
      return
    }

    setSubmitting(true)
    try {
      const response = await apiPost(`company/${companyId}/bulk-price-control`, {
        branchIds: selectedBranchIds,
        isDailyActive,
        isWeeklyActive,
        isMonthlyActive,
        isYearlyActive,
      })

      if (response?.success) {
        notify.success(t('priceControlModal.messages.updateSuccess'))
        onClose()
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  {t('priceControlModal.title')}
                </Dialog.Title>

                <div className="mt-6 space-y-6">
                  <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">{t('priceControlModal.form.branches')}</label>
                    <Selection
                      multiple
                      title={t('priceControlModal.form.selectBranches')}
                      list={branchOptions}
                      value={selectedBranchIds}
                      onChange={ids => setSelectedBranchIds(ids || [])}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('priceControlModal.form.supportDaily')}</span>
                      <Switch value={isDailyActive} onChange={setDaily} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('priceControlModal.form.supportWeekly')}</span>
                      <Switch value={isWeeklyActive} onChange={setWeekly} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('priceControlModal.form.supportMonthly')}</span>
                      <Switch value={isMonthlyActive} onChange={setMonthly} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('priceControlModal.form.supportYearly')}</span>
                      <Switch value={isYearlyActive} onChange={setYearly} />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
                    onClick={onClose}
                  >
                    {t('priceControlModal.buttons.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-50"
                    onClick={handleUpdate}
                  >
                    {submitting ? t('priceControlModal.buttons.updating') : t('priceControlModal.buttons.update')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default PriceControlModal
